import React from 'react';
import { analyticsStats, revenueData, priceDistribution, topSearches } from '../data/mockData';
import { Sparkles, TrendingUp, Eye, ArrowUpRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const iconMap = {
  Sparkles: Sparkles,
  TrendingUp: TrendingUp,
  Eye: Eye,
};

const AnalyticsSection = () => {
  return (
    <section id="analitika" className="py-24 lg:py-32 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <span className="text-xs font-bold tracking-[0.2em] text-blue-600 uppercase">
            Analitika
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Valós idejű ROI követés
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Átlátható statisztikák a konverzióktól a keresési mintázatokig.
          </p>
        </div>

        {/* ── MOBILE ANALYTICS ── */}
        <div className="lg:hidden space-y-4 max-w-lg mx-auto pointer-events-none select-none">
          {/* Live badge + main stat */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-xl shadow-blue-600/25">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
              <span className="text-xs font-semibold text-blue-100">Élő dashboard · Utolsó 30 nap</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {analyticsStats.map((stat) => {
                const Icon = iconMap[stat.icon];
                return (
                  <div key={stat.label} className="text-center">
                    <div className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center mx-auto mb-2">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xl font-extrabold text-white leading-none">{stat.value}</p>
                    <p className="text-[10px] text-blue-200 mt-1 leading-tight">{stat.label}</p>
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-green-300 mt-1">
                      <ArrowUpRight className="w-2.5 h-2.5" />{stat.change}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Revenue chart */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Bevétel növekedés</h3>
              <span className="flex items-center gap-0.5 text-xs font-semibold text-green-600"><ArrowUpRight className="w-3 h-3" />+127%</span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={30} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Top searches */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Top keresések</h3>
            <div className="space-y-3">
              {topSearches.map((item) => (
                <div key={item.rank} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{item.rank}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{item.query}</span>
                      <span className="text-xs font-semibold text-gray-500">{item.count}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(item.count / 1247) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── DESKTOP DASHBOARD MOCKUP ── */}
        <div className="hidden lg:block max-w-5xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden pointer-events-none select-none">
          {/* Dashboard header */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-sm font-bold text-gray-900">Dashboard</span>
              <span className="text-sm text-gray-400">Utolsó 30 nap</span>
            </div>
            <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-0.5">
              <span
                className="px-3 py-1.5 text-xs font-medium rounded-md transition-all bg-white text-gray-900 shadow-sm"
              >
                Áttekintés
              </span>
              <span
                className="px-3 py-1.5 text-xs font-medium rounded-md transition-all text-gray-500"
              >
                Részletes
              </span>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6">
            {analyticsStats.map((stat) => {
              const Icon = iconMap[stat.icon];
              return (
                <div key={stat.label} className="p-5 border border-gray-100 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="flex items-center gap-0.5 text-xs font-semibold text-green-600">
                      <ArrowUpRight className="w-3 h-3" />
                      {stat.change}
                    </span>
                  </div>
                  <p className="text-2xl font-extrabold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
                </div>
              );
            })}
          </div>

          {/* Charts row */}
          <div className="grid lg:grid-cols-5 gap-4 px-6 pb-6">
            {/* Revenue chart */}
            <div className="lg:col-span-3 p-5 border border-gray-100 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-900">Bevétel növekedés (ezer Ft)</h3>
                <span className="flex items-center gap-0.5 text-xs font-semibold text-green-600">
                  <ArrowUpRight className="w-3 h-3" />
                  +127%
                </span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Right column */}
            <div className="lg:col-span-2 space-y-4">
              {/* Price distribution */}
              <div className="p-5 border border-gray-100 rounded-xl">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Árkategória eloszlás (%)</h3>
                <ResponsiveContainer width="100%" height={100}>
                  <BarChart data={priceDistribution}>
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Bar dataKey="value" fill="#2563EB" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Top searches */}
              <div className="p-5 border border-gray-100 rounded-xl">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Top keresések</h3>
                <div className="space-y-2">
                  {topSearches.map((item) => (
                    <div key={item.rank} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-blue-600 w-4">{item.rank}.</span>
                        <span className="text-xs text-gray-700">{item.query}</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-500">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsSection;
